import { AfterViewInit, computed, Directive, ElementRef, HostBinding, inject, input } from '@angular/core';
import { assertExhaustive } from 'src/app/core/assert-utils';
import { getIsNotNullable, Maybe } from 'src/app/core/nullable-utils';
import { getIsFiniteNumber } from 'src/app/core/primitive-utils';

type StandardSize = 's' | 'm' | 'l' | 'xl';
type CustomSize = string;
type AnySize = StandardSize | CustomSize;

/**
 * Used to define the size of any mat-icon.
 * See {@link StandardSize} for a list of all standard sizes.
 * Custom size is supported simply as a string and any valid
 * css size values will work, e.g. '12px' or '1.25rem'.
 */
@Directive({
  selector: 'mat-icon, mat-icon[size]',
})
export class IconSizeDirective implements AfterViewInit {
  readonly iconRef: ElementRef<HTMLElement> = inject(ElementRef);

  readonly size = input<AnySize>('s');

  readonly #computedSize = computed(() => {
    const size = this.size();
    return getIsIconSize(size) ? getSize(size) : size;
  });

  @HostBinding('style.min-height')
  @HostBinding('style.height')
  get height() {
    return this.#computedSize();
  }
  @HostBinding('style.min-width')
  @HostBinding('style.width')
  get width() {
    return this.#computedSize();
  }
  @HostBinding('style.margin')
  get margin() {
    const parentElement = this.iconRef.nativeElement.parentElement;
    if (!getIsHTMLElementButton(parentElement)) {
      return;
    }
    // When parent button has no text, remove excess margins
    return getHasMatButtonText(parentElement) ? this.iconRef.nativeElement.style.margin : 'unset';
  }

  ngAfterViewInit(): void {
    const svg = this.iconRef.nativeElement.querySelector<SVGElement>('svg');
    if (getIsNotNullable(svg)) {
      // Center the icon to preserve all rendered pixels.
      // This could also be achieved by flexing the host element, but such hard styles on the parent
      // can conflict with animations, so we make the child SVG position itself instead.
      svg.style.verticalAlign = 'top';

      if (getHasParentAttributeMatIconButton(this.iconRef.nativeElement)) {
        // mat-icon-button directive overrides the icon size, so we reset it appropriately
        svg.style.height = this.#computedSize();
        svg.style.width = this.#computedSize();
      }
    }

    const parentElement = this.iconRef.nativeElement.parentElement;

    if (
      getIsHTMLElementButton(parentElement) &&
      !getHasMatButtonText(parentElement) &&
      !parentElement.classList.contains('profile-status-selector__submit')
    ) {
      // Apply compact sizing only to icon-only buttons.
      parentElement.style.minWidth = 'unset';
      parentElement.style.padding = '0 8px';
    }
  }
}

function getIsIconSize(size: AnySize): size is StandardSize {
  try {
    return !!getSize(size as StandardSize);
  } catch {
    return false;
  }
}

function getSize(size: StandardSize): string {
  switch (size) {
    case 's':
      return '16px';
    case 'm':
      return '20px';
    case 'l':
      return '24px';
    case 'xl':
      return '32px';
    default:
      assertExhaustive(size);
  }
}

function getIsHTMLElementButton(element: Maybe<HTMLElement>): element is HTMLButtonElement {
  return element?.nodeName === 'BUTTON';
}

function getHasMatButtonText(button: HTMLButtonElement) {
  return ['.mdc-button__label', '.mat-mdc-menu-item-text']
    .map(selector => button.querySelector<HTMLSpanElement>(selector))
    .some(el => getIsNotNullable(el) && getHasText(el));
}

function getHasParentAttributeMatIconButton(element: HTMLElement) {
  return getIsNotNullable(element.parentElement?.getAttribute('mat-icon-button'));
}

function getHasText(element: HTMLSpanElement) {
  return getIsPositiveNumber(element.textContent?.length);
}

function getIsPositiveNumber(length: number | undefined) {
  return getIsFiniteNumber(length) && length > 0;
}
